import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import ImmersiveLayout from '../components/ImmersiveLayout';
import { Package, ArrowRight, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const STATUS_TONE = {
  pending_payment: { bg: 'bg-amber-400/10', text: 'text-amber-300', label_id: 'Menunggu Pembayaran', label_en: 'Pending Payment' },
  paid: { bg: 'bg-seikat-lime/15', text: 'text-seikat-lime', label_id: 'Dibayar', label_en: 'Paid' },
  accepted: { bg: 'bg-blue-400/10', text: 'text-blue-300', label_id: 'Diterima', label_en: 'Accepted' },
  in_progress: { bg: 'bg-seikat-pink/15', text: 'text-seikat-pink', label_id: 'Dirangkai', label_en: 'Crafting' },
  packed: { bg: 'bg-seikat-pink/15', text: 'text-seikat-pink', label_id: 'Dikemas', label_en: 'Packed' },
  out_for_delivery: { bg: 'bg-seikat-lime/15', text: 'text-seikat-lime', label_id: 'Dikirim', label_en: 'Out for Delivery' },
  delivered: { bg: 'bg-seikat-lime/15', text: 'text-seikat-lime', label_id: 'Terkirim', label_en: 'Delivered' },
  completed: { bg: 'bg-seikat-pink/15', text: 'text-seikat-pink', label_id: 'Selesai', label_en: 'Completed' },
  cancelled: { bg: 'bg-red-500/10', text: 'text-red-300', label_id: 'Dibatalkan', label_en: 'Cancelled' }
};

export default function MyOrdersPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const id = language === 'id';

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    axios.get(`${API}/orders`, { withCredentials: true })
      .then(r => setOrders(r.data.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const fmt = (p) => new Intl.NumberFormat('id-ID').format(p);
  const fmtDate = (d) => new Date(d).toLocaleDateString(id ? 'id-ID' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <ImmersiveLayout>
      <section className="px-6 lg:px-16 pt-24 pb-32" data-testid="my-orders-page">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-lime" />
              <span className="eyebrow-immersive">{id ? 'Riwayat' : 'History'}</span>
            </div>
            <h1 className="mt-4 font-heading text-5xl lg:text-6xl text-seikat-bone leading-[0.95]">
              {id ? <>Setiap <em>rangkaian</em> yang pernah kami antar.</> : <>Every <em>weave</em> we've ever sent.</>}
            </h1>
          </motion.div>

          {loading ? (
            <div className="mt-20 flex justify-center"><Loader2 size={24} className="animate-spin text-seikat-pink" /></div>
          ) : orders.length === 0 ? (
            <div className="mt-16 glass-dark rounded-2xl p-12 text-center" data-testid="orders-empty">
              <Package size={36} className="mx-auto text-seikat-pink/60 mb-6" strokeWidth={1.4} />
              <h2 className="font-heading text-3xl text-seikat-bone">{id ? 'Belum ada pesanan' : 'No orders yet'}</h2>
              <p className="font-body text-seikat-bone/60 mt-3 max-w-md mx-auto">
                {id ? 'Mari mulai dengan rangkaian pertamamu.' : 'Let\'s begin with your first weave.'}
              </p>
              <Link to="/catalog">
                <button className="btn-pill btn-pill--solid mt-8" data-testid="browse-catalog-btn">
                  {id ? 'Jelajahi Bouquet' : 'Browse Bouquets'} <ArrowRight size={14} />
                </button>
              </Link>
            </div>
          ) : (
            <div className="mt-14 space-y-4">
              {orders.map((order, i) => {
                const tone = STATUS_TONE[order.status] || STATUS_TONE.pending_payment;
                return (
                  <motion.div
                    key={order.order_id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.04, ease: EASE }}
                    className="glass-dark rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-5"
                    data-testid={`order-${order.order_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <code className="font-mono text-xs text-seikat-bone-muted bg-white/5 px-2 py-1 rounded">{order.order_id}</code>
                        <span className={`text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full ${tone.bg} ${tone.text}`}>
                          {id ? tone.label_id : tone.label_en}
                        </span>
                      </div>
                      <h3 className="font-heading text-2xl text-seikat-bone">
                        {order.items?.length} {id ? 'tangkai' : (order.items?.length === 1 ? 'item' : 'items')}
                      </h3>
                      <p className="font-body text-xs text-seikat-bone-muted mt-1">{fmtDate(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-5">
                      <div className="text-right">
                        <p className="font-heading text-2xl text-seikat-pink">Rp {fmt(order.total)}</p>
                      </div>
                      <Link to={`/orders/${order.order_id}`}>
                        <button className="btn-pill" data-testid={`view-order-${order.order_id}`} data-magnet>
                          {id ? 'Detail' : 'Detail'} <ArrowRight size={12} />
                        </button>
                      </Link>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </ImmersiveLayout>
  );
}
